import { isP2pCall } from 'util/getCallType';
import { decodeAuthToken } from 'util/decodeAuthToken';
import { Participant } from 'livekit-client';
import { selectLocalParticipant, selectParticipants, selectRoomMetadata } from 'state/selectors';
import { selectCurrentUser } from 'state/selectors/app';
import { selectAuthToken } from 'state/selectors/auth';
import { getParticipantUserIdFromMetadata } from 'useCase/participant/useParticipantMetadata';
import { parseModeratorsFromMetadata } from '../utils/parseModerators';
import { determineModeratorType } from '../utils/determineModeratorType';
import type { RoomModerator } from '../types';

const getModeratorsFromRoomMetadata = (): RoomModerator[] => {
    const metadata = selectRoomMetadata();

    return parseModeratorsFromMetadata(metadata);
};

const filterActiveGuestModerators = (moderators: RoomModerator[], participants: Participant[]): RoomModerator[] => {
    const activeIdentities = new Set(participants.map((p) => p.identity).filter(Boolean));
    return moderators.filter((moderator) => {
        const type = determineModeratorType(moderator);
        if (type !== 'GUEST') return true;
        return moderator.identity != null && activeIdentities.has(moderator.identity);
    });
};

const getParticipantUserId = (participant: Participant): string | null =>
    getParticipantUserIdFromMetadata(participant.metadata);

/**
 * Проверяет, соответствует ли модератор участнику: USER по userId, GUEST по identity.
 */
const isModeratorMatch = (
    moderator: RoomModerator,
    participantUserId: string | null,
    participantIdentity: string | null,
): boolean => {
    if (!moderator || typeof moderator !== 'object') {
        console.warn('[Moderation] некорректный объект модератора', { moderator });
        return false;
    }

    const type = determineModeratorType(moderator);
    if (!type) {
        console.warn('[Moderation] не удалось определить тип модератора', {
            moderatorType: moderator.type,
            moderatorUserId: moderator.userId,
            moderatorIdentity: moderator.identity,
        });
        return false;
    }

    const moderatorUserId = moderator.userId ?? null;
    const moderatorIdentity = moderator.identity ?? null;

    if (type === 'USER') {
        if (moderatorUserId == null) return false;
        return participantUserId != null && moderatorUserId === participantUserId;
    }

    if (type === 'GUEST') {
        return moderatorIdentity != null && participantIdentity != null && moderatorIdentity === participantIdentity;
    }

    return false;
};

export const isModeratorParticipant = (participant: Participant): boolean => {
    const participants = selectParticipants();
    const isP2p = isP2pCall();

    // в P2P звонке модератор может быть только если участников > 2
    // в групповом звонке модератор может быть всегда
    if (isP2p && participants.length <= 2) return false;

    const moderators = filterActiveGuestModerators(getModeratorsFromRoomMetadata(), participants);
    if (moderators.length === 0) return false;

    const participantUserId = getParticipantUserId(participant);
    const participantIdentity = participant.identity || null;

    return moderators.some((m) => isModeratorMatch(m, participantUserId, participantIdentity));
};

export const isMainModeratorParticipant = (participant: Participant): boolean => {
    const participants = selectParticipants();
    const isP2p = isP2pCall();
    if (isP2p && participants.length <= 2) return false;

    const moderators = filterActiveGuestModerators(getModeratorsFromRoomMetadata(), participants);
    const participantUserId = getParticipantUserId(participant);
    const participantIdentity = participant.identity || null;

    return moderators.some((m) => m.role === 'main' && isModeratorMatch(m, participantUserId, participantIdentity));
};

export const isLocalUserModerator = (): boolean => {
    const localParticipant = selectLocalParticipant();
    if (!localParticipant) return false;

    return isModeratorParticipant(localParticipant);
};

/**
 * Проверяет модератора по userId из metadata без LiveKit participant.
 * Используется на prejoin после F5 из комнаты, когда localParticipant ещё null,
 * но roomInfoStore.metadata сохранилась (IN_ROOM флаг предотвратил clearMetadata).
 */
export const isLocalUserModeratorByUserId = (): boolean => {
    const metadata = selectRoomMetadata();
    const embeddedUser = selectCurrentUser();
    const authToken = selectAuthToken();
    const decoded = authToken ? decodeAuthToken(authToken) : null;
    const localUserId = embeddedUser?.id ?? decoded?.userId ?? null;
    const moderators = metadata ? parseModeratorsFromMetadata(metadata) : [];

    if (!metadata || localUserId == null) return false;
    return moderators.some((m) => m.userId != null && Number(m.userId) === localUserId);
};

export const isGuestParticipant = (participant: Participant): boolean => {
    return getParticipantUserId(participant) == null;
};
